'use client';

import { createContext, use, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * The canonical clickable one-line list row (LF-6062) — extracted from the
 * `/scribe` `RecordingRow` idiom (`components/ai-scribe/home/recording-row.tsx`),
 * the reference for the app's "new list" row treatment. Fixes row height,
 * typography, hover, and keyboard/`role` semantics in one place via a slot
 * API: `leading` (glyph/avatar) · `title` · `inline` (pills/chips beside the
 * title) · `meta` (muted trailing text) · `status` (a right-aligned state pill,
 * against the actions) · `actions` (right-aligned CTA/avatar/overflow, isolated
 * from the row's own click).
 *
 * Separators come from the enclosing `ListSection` (`shared/page-shell`),
 * never this row.
 *
 * Beneficiary list (BENTAB-4) is the first adopter. `recording-row` and
 * `redemption-list` are retrofitted onto this primitive in a deferred
 * follow-up — this ticket does not change them.
 */
/**
 * **The row rhythm and title typography every item list shares** (VALE-863).
 *
 * Exported because three lists still hand-roll their own row — Inventory
 * (`balance-sheet/components/inventory-item-row`), Discovery
 * (`discovery/discovery-item-row`) and Redemption
 * (`redemption/redemption-item-row`) — and before this they each owned a
 * *different* vertical padding: `py-2`, `py-2` and `py-2.5`. Identical type
 * sizes, three row heights, which is exactly the "differently-authored screens"
 * Tom's 27 Aug review named.
 *
 * They bind to these instead of restating a literal, so {@link ListRow} stays
 * the single owner of the value even on the surfaces that have not adopted the
 * component yet. **Horizontal padding is deliberately NOT here** — those rows
 * sit in containers with their own gutters (`px-4`) where this component uses
 * `pl-1.5 pr-0`, so sharing it would break their alignment rather than fix it.
 *
 * Full adoption of {@link ListRow} remains the deferred follow-up the
 * standardised-components registry already records; this only stops the three
 * from drifting apart on the dimension the review flagged.
 */
export const LIST_ROW_PADDING_Y = 'py-2';

/**
 * The row's primary text — see {@link LIST_ROW_PADDING_Y} for why it is shared.
 *
 * **13px since VALE-1534**, chosen by the owner against the running product
 * from three candidates rendered side by side on the Inventory tab. It was
 * 14px, which was also the supporting text's size and the money figure's, so a
 * row had one size doing three jobs and the name did not lead its own row.
 */
export const LIST_ROW_TITLE_TEXT = 'text-13px font-medium';

/**
 * A row's muted supplementary text (counts, summaries, trailing meta, a status
 * context line).
 *
 * **12px, and this file used to hold two answers for that one role** — the
 * token said `text-sm` (14px) for the three hand-rolled rows while {@link ListRow}'s
 * own `meta` slot rendered a `text-12px` literal, so the primitive that exists
 * to stop those rows drifting disagreed with itself (VALE-1436). The slot now
 * binds to this constant, which is the whole point of exporting it.
 *
 * 14px was also the row's own title size, so a row had one size doing four
 * jobs — name, count, wait and section header — and reserved its only smaller
 * size for the status badge, leaving supplementary text LARGER than the state
 * token beside it. At 12px the name is the only 14px thing on the row.
 */
export const LIST_ROW_META_TEXT = 'text-12px';

/**
 * The supporting text beside a row's title — an address, an account number
 * (VALE-1534).
 *
 * **Not the same role as {@link LIST_ROW_META_TEXT}, which is why it is a
 * second constant rather than a reuse of that one.** Meta is the muted note
 * trailing the row and sits at the app's one small size. This sits immediately
 * beside the title, reads as part of the row's identity — "47 Ironbark Road,
 * *Mosman NSW*" is one name, not a name and a footnote — and so steps down
 * with the title rather than staying put when the rhythm changes.
 */
export const LIST_ROW_SUPPORTING_TEXT = 'text-12-5px';

/** A row's leading glyph, at the size every item list shares. */
export const LIST_ROW_ICON = 'h-4 w-4';

/** The rule between adjacent rows, at the weight every item list shares. */
export const LIST_ROW_DIVIDER = 'divide-border-subtle';

/** The fill behind a list's grey section-header strip. */
export const LIST_HEADER_FILL = 'bg-surface-subtle';

/**
 * The distance from a row's left edge to its TITLE, as a left margin.
 *
 * What a detail block rendered under the row indents by, so its first column
 * starts under the title rather than under the glyph. It is the row's own
 * `px-4` plus the glyph's box plus the `gap-2` between them, so it MOVES when
 * the glyph's box does, which is exactly why it lives here beside
 * {@link LIST_ROW_ICON} rather than as a literal in each block (VALE-1494:
 * three blocks each restated `ml-10`, and the tile below widened the column
 * they were all measuring).
 */
export const LIST_ROW_TITLE_OFFSET = 'ml-10';

/** {@link LIST_ROW_TITLE_OFFSET}, as padding, for a row nested under another. */
export const LIST_ROW_NESTED_INDENT = 'pl-10';

/**
 * A row's padding when **its own detail block renders directly beneath it**.
 *
 * The bottom half of {@link LIST_ROW_PADDING_Y} exists to separate this row
 * from the NEXT one. On the AI review every row is followed by its own
 * expanded detail, so that padding is no longer separating two rows, it is
 * separating a name from the values belonging to it, and at the row's full
 * height it pushed the two apart further than the gaps INSIDE the block
 * (VALE-1494). The top half is unchanged: that one is still doing its job.
 */
export const LIST_ROW_PADDING_WITH_DETAIL = 'pt-2 pb-1';

/**
 * A row control's box — the `⋯` menu button, an inline pill-shaped action.
 *
 * **This is what actually sets a row's height, not the text** (VALE-1534).
 * Measured on the Inventory tab before any of this changed: the row was
 * `py-2` (6.8px each side) around a 24.69px `h-7` menu button, for 38.28px —
 * while the title's own line box is only 20px. So a density step that moves
 * padding and type and leaves the control alone moves the row by *nothing*,
 * because the control is still the tallest thing in it. Every rhythm below
 * therefore names a control height beside its padding, and the row binds to it.
 */
export const LIST_ROW_CONTROL = 'h-6 w-6';

/**
 * A row's status chip — its text size AND its vertical padding.
 *
 * Both, from one constant, because both feed the chip's height and the chip is
 * one of the three things that can be the tallest in the row (VALE-1534).
 *
 * **No sub-floor text size any more (VALE-1725).** This used to sit `Badge`
 * and `StatusBadge` below their own 12px floor (`text-11px`) to keep the chip
 * under {@link LIST_ROW_CONTROL}'s ~20.4px height — legitimate before the
 * shared components' text carried the browser's default `text-xs` line-height
 * (16px, which alone would make an unpadded chip ~21.9px, two-thirds of a
 * 30px row). `Badge`/`StatusBadge` now render at `leading-none`, so a 12px
 * chip's own box is ~12px tall — comfortably under the control's height with
 * room to spare — and there is no longer a reason to drop below the shared
 * floor to make it fit. Every pill in the app, including this one, now reads
 * at the one size the components own.
 *
 * **`whitespace-nowrap` is load-bearing, not tidiness.** `StatusBadge` does not
 * set it, and since VALE-1534 a chip sits in a fixed-width column rather than
 * in the free-flowing name text — so a label wider than the column wraps to two
 * lines, which breaks the row it is on and, because the row is then taller than
 * its neighbours, the uniform row height the density rests on. It has to fail
 * as a visible overflow instead. `__tests__/status-column-fit.test.tsx` guards
 * the widths; this guards the wrap.
 */
export const LIST_ROW_CHIP = 'text-xs py-0 whitespace-nowrap';

/**
 * A money cell's figure — its size AND its vertical padding, for the same
 * reason as {@link LIST_ROW_CHIP}.
 *
 * The padding is not decoration: an editable figure is a button with a hover
 * fill, and the fill needs room. At today's rhythm that button is 23.4px,
 * just under the `⋯` control, so it costs nothing. At a denser one it
 * overtook the control and became the row's tallest element — on the rows that
 * happen to have an editable figure and not on the others, so the list
 * rendered at TWO row heights at once, which reads far worse than being a
 * couple of pixels off a target.
 */
export const LIST_ROW_VALUE_TEXT = 'text-13px py-0';

/* ─── The roomy rhythm (VALE-1494) ──────────────────────────────────────────
   The AI-extract review reads as a list of PROPOSALS a person is deciding
   about, one row at a time, rather than a register they scan for one entry.
   Round 5 of that surface's design asked for more air between rows, a heavier
   title to anchor each one, and a larger glyph beside it.

   These live here, beside the values they are an alternative to, because
   {@link LIST_ROW_PADDING_Y} exists precisely so the app's lists cannot each
   own a different row height — and answering VALE-1494 with a literal inside
   the review components would have re-created that drift on the very tokens
   built to prevent it. One file still owns both rhythms; a surface chooses
   between them by wrapping its lists in {@link ListRowRhythmProvider}.

   **`compact` is the default, so the ROOMY rhythm reaches nothing but the
   review.** Whether it should also carry to the matter's Inventory tab and
   Current View — which render these same rows — is an open design question on
   VALE-1494, settled with both surfaces on screen. When the answer is yes, it
   is this file's default that changes, not another call site.

   **The `own` escape hatch is gone, and VALE-863's convergence is done**
   (VALE-1534). VALE-1494 found `contact-row`, `document-row` and
   `entitlement-row` still drawing their own `py-2` against a shared token of
   `py-2.5`, and rightly refused to move three tabs as a side effect of a
   review-surface change: each row passed its own padding to
   `useListRowRhythm(own)` instead, and the argument carried the deferral.

   VALE-1534 is the ticket that decision was waiting for. It sets the app-wide
   density deliberately, with the owner judging it in the running product, so
   the surfaces converge the other way: {@link LIST_ROW_PADDING_Y} is now
   `py-2` — the value the Inventory and Contacts tabs already rendered, and the
   measured basis of the 38.28px row the ladder below is re-based on — and the
   `own` parameter no longer exists. Discovery, Redemption, Beneficiaries and
   the date-of-death inventory row move DOWN to it from `py-2.5`, which is the
   direction this ticket was asking for anyway. */
export const LIST_ROW_PADDING_Y_ROOMY = 'py-3.5';

/**
 * The row's primary text where the row is the thing being decided about — one
 * step heavier than {@link LIST_ROW_TITLE_TEXT}, so the title anchors the row
 * against the values and badges beside it.
 */
export const LIST_ROW_TITLE_TEXT_ANCHORED = 'text-sm font-semibold';

/** A row's leading glyph, scaled up to hold its own against the text beside it. */
export const LIST_ROW_ICON_ROOMY = 'h-5 w-5';

/**
 * The tile a roomy row's glyph sits in (VALE-1494).
 *
 * Four different kinds of glyph land in this slot across the review, at four
 * different intrinsic weights: a line-drawn `User`, a filled institution
 * favicon, a coloured file-type icon and a `Users` group mark. Unhoused, they
 * read as four unrelated marks down a column that is meant to be one column.
 * A single tile behind all of them makes the column the constant and the glyph
 * inside it the variable.
 *
 * `--muted` matches the section-header strip, so the two greys on the surface
 * are one grey. 32px against a 20px glyph, which leaves the 6px of breathing
 * room the tile needs to read as a tile rather than as a tight box.
 *
 * `rounded-sm` is the SQUAREST rung on the radius scale (8px, where this app's
 * `--radius` puts `lg` at 12px). It shipped at `rounded-lg` for one round and
 * read as a pill-ish blob at that size: a radius is a proportion of the box it
 * is on, and 12px on a 32px tile is over a third of the side. The tile wants to
 * read as a square holding a mark.
 */
export const LIST_ROW_ICON_TILE_ROOMY =
  'bg-muted flex h-8 w-8 shrink-0 items-center justify-center rounded-sm';

/**
 * {@link LIST_ROW_TITLE_OFFSET} for the roomy rhythm: `px-4` (16) + the tile
 * (32) + `gap-2` (8) = 56px.
 */
export const LIST_ROW_TITLE_OFFSET_ROOMY = 'ml-14';

/** {@link LIST_ROW_TITLE_OFFSET_ROOMY}, as padding. */
export const LIST_ROW_NESTED_INDENT_ROOMY = 'pl-14';

/**
 * A roomy row's padding when its own detail block renders beneath it.
 *
 * See {@link LIST_ROW_PADDING_WITH_DETAIL}. The bottom is the SAME `pb-1` at
 * both rhythms rather than scaling with the top: it is no longer a share of
 * the row's height, it is the gap between a name and its own first value, and
 * that reads best matched to the gaps inside the block, which do not change
 * with the rhythm either.
 */
export const LIST_ROW_PADDING_WITH_DETAIL_ROOMY = 'pt-3.5 pb-1';

/**
 * The rule between adjacent rows, one step stronger.
 *
 * `--border-subtle` is `oklch(0.967 0 0)` against a white card, which is a
 * hairline meant to be felt rather than seen — right for a register, and on a
 * list of proposals a reader is deciding about row by row it left the boundary
 * between two rows genuinely hard to find. `--border` is the next rung up.
 */
export const LIST_ROW_DIVIDER_STRONG = 'divide-border';

/**
 * The fill behind a section-header strip, one step stronger.
 *
 * `--surface-subtle` is `oklch(0.985 …)` against a `--card` of `oklch(1 …)`, a
 * 1.5% step in lightness — enough to read as a strip on a page that is mostly
 * text, and not enough on a review where the heading is the only thing telling
 * four stacked lists apart. `--muted` is `oklch(0.9702 …)`, roughly double the
 * separation, and it is the fill the app already uses where a band has to be
 * seen rather than felt. The heading's own `--muted-foreground` is asserted
 * against `--muted` at 4.58:1 by the contrast gate, so the type on it is
 * already covered.
 */
export const LIST_HEADER_FILL_STRONG = 'bg-muted';

/**
 * **Where a list row hangs its marks** (VALE-1737).
 *
 * Two answers, and the surface decides rather than the row:
 *
 * - **`column`** — a dedicated right-aligned cluster against the row's actions.
 *   VALE-1725's app-wide standardisation, and the default, so every list that
 *   ticket moved stays exactly where it put them.
 * - **`inline`** — in flow immediately after the row's own text, which is where
 *   these marks sat before VALE-1725 and where the **AI-extract review** wants
 *   them back.
 *
 * **Why the review is different, rather than behind.** A row on a review is not
 * a record with a state to compare down a column — it is a proposal, and its
 * mark says something about the proposal's own text: this name is new, this
 * institution needs checking. Read beside the thing it qualifies, it is part of
 * the sentence; parked at the right edge it becomes a column of pills the eye
 * scans separately from the values it is meant to be reading. On the Contacts
 * tab the opposite holds, which is what VALE-1725 was right about: there the
 * mark IS the fact being compared row to row, and a column is what makes the
 * comparison possible.
 *
 * So this is one decision with two correct answers, not a surface lagging a
 * standard — and it is a property of the LIST, never of the row, which is why
 * it arrives by context. A row kind renders on both kinds of surface; asking
 * each call site to pass a prop is how the four rows ended up with four
 * answers in the first place.
 */
export type ListRowMarkPlacement = 'column' | 'inline';

const ListRowMarkPlacementContext =
  createContext<ListRowMarkPlacement>('column');

/**
 * Declare where the rows inside draw their marks.
 *
 * The AI-extract review's list wrappers are the only consumers — see
 * `components/ai-review/CLAUDE.md`. Everything else inherits `column` by
 * saying nothing, so adopting this moved no surface VALE-1725 touched.
 */
export function ListRowMarkPlacementProvider({
  placement,
  children
}: {
  placement: ListRowMarkPlacement;
  children: ReactNode;
}) {
  return (
    <ListRowMarkPlacementContext value={placement}>
      {children}
    </ListRowMarkPlacementContext>
  );
}

export function useListRowMarkPlacement(): ListRowMarkPlacement {
  return use(ListRowMarkPlacementContext);
}

/**
 * The right-aligned form of the mark cluster — VALE-1725's column.
 *
 * `ml-auto` is the row's one pusher, so the cluster lands at the right edge
 * whatever the row's middle holds and the actions slot renders after it in
 * normal flow. A row on this placement must NOT also put `ml-auto` on its
 * actions: two pushers split the free space between them instead of sending
 * one element to the edge.
 */
export const LIST_ROW_STATUS_COLUMN =
  'ml-auto flex shrink-0 flex-wrap items-center justify-end gap-1.5';

/**
 * The in-flow form — where these marks sat before VALE-1725, and where the AI
 * review puts them back.
 *
 * No pusher, so the row's own text continues past it and whatever the row uses
 * to right-align its actions (an `ml-auto` on the actions slot, or a `flex-1`
 * title block) does that job unchanged.
 */
export const LIST_ROW_MARKS_INLINE =
  'flex shrink-0 flex-wrap items-center gap-1.5';

/**
 * A row's marks, in whichever form the enclosing list asked for.
 *
 * **A row renders this in one of two POSITIONS in its own JSX**, because
 * placement is not only a class: inline means *after the row's text*, and no
 * amount of styling moves a node the row wrote at the far right back into the
 * middle of its sentence. So a row renders its marks once, at the position the
 * placement calls for, and hands the same children either way — the four rows
 * below all follow that shape.
 *
 * **It renders even when it holds nothing, on the `column` placement, and that
 * is deliberate**: the column is what pushes the row's actions to the right
 * edge, so a row whose marks all evaluated to nothing still has to spend it or
 * the `⋯` slides left into the title on exactly the rows that have least to
 * say. Inline it has no such job, so it stays out of the way.
 */
export function ListRowMarks({
  children,
  className,
  testId
}: {
  children?: ReactNode;
  className?: string;
  /**
   * Names the marks node for a spec (VALE-1355).
   *
   * A row's marks and its expanded block both sit inside the row's own root, so
   * the row-level testid cannot answer "what does the LINE assert" - which is
   * the question the AI review's reject coverage is about, and the block below
   * legitimately keeps a rejected value on screen.
   */
  testId?: string;
}) {
  const placement = useListRowMarkPlacement();
  return (
    <div
      data-testid={testId}
      className={cn(
        placement === 'inline' ? LIST_ROW_MARKS_INLINE : LIST_ROW_STATUS_COLUMN,
        className
      )}
    >
      {children}
    </div>
  );
}

/* ─── What the roomy rhythm keeps (VALE-1534) ──────────────────────────────
   The four values below were the app-wide defaults until VALE-1534 chose a
   denser set. The AI review is not part of that decision — it reads as a list
   of proposals a person is deciding about one at a time, which is what the
   roomy rhythm is for — so it keeps them, spelled out here rather than
   referencing the compact constants it used to share.

   **Sharing them was safe only while they agreed.** `roomy` read
   `LIST_ROW_CONTROL`, `LIST_ROW_CHIP`, `LIST_ROW_VALUE_TEXT` and
   `LIST_ROW_SUPPORTING_TEXT` directly, so changing the default for the
   Inventory tab would have silently pulled the review's controls, chips and
   figures denser too — a surface nobody in this ticket looked at, moved by a
   ticket that never mentions it.                                            */

/** The `⋯` control, at the size the roomy rhythm keeps. */
export const LIST_ROW_CONTROL_ROOMY = 'h-7 w-7';

/** A status chip, at the size the roomy rhythm keeps. */
export const LIST_ROW_CHIP_ROOMY = 'text-xs py-0.5 whitespace-nowrap';

/** A money figure, at the size the roomy rhythm keeps. */
export const LIST_ROW_VALUE_TEXT_ROOMY = 'text-sm py-0.5';

/** Supporting text beside the title, at the size the roomy rhythm keeps. */
export const LIST_ROW_SUPPORTING_TEXT_ROOMY = 'text-sm';

/** Which rhythm a list renders at. */
export type ListRowRhythmName = 'compact' | 'roomy';

export interface ListRowRhythm {
  /** Vertical padding utility — the row's height. */
  paddingY: string;
  /**
   * The same, for a row whose own detail block renders beneath it — see
   * {@link LIST_ROW_PADDING_WITH_DETAIL}.
   */
  paddingYWithDetail: string;
  /** The title's size and weight together, for a title drawn as one span. */
  titleText: string;
  /**
   * The title's weight alone.
   *
   * For a title whose parts are weighted differently — the entitlement row's
   * sentence emphasises its subject and object and leaves the verb between them
   * normal — so those parts can take the weight without restating the size.
   */
  titleWeight: string;
  /**
   * The supporting text beside the title — an address, an account number, the
   * thing that tells two similarly-named rows apart (VALE-1534).
   *
   * Distinct from {@link ListRowRhythm.metaText}, which is the muted trailing
   * note. This one sits next to the title and reads as part of the row's
   * identity, so it steps down WITH the title rather than staying at the
   * app's one small size.
   */
  supportingText: string;
  /** A row's muted trailing text — a count, a note preview, a date. */
  metaText: string;
  /** The leading glyph's box. */
  icon: string;
  /**
   * A row control's box — the `⋯` menu, an inline pill action.
   *
   * **The tallest thing in the row, so this is what the height actually
   * follows** — see {@link LIST_ROW_CONTROL}. A rhythm that steps the padding
   * and the type but not this one does not change the row height at all.
   */
  control: string;
  /** The status chip's text size and padding — see {@link LIST_ROW_CHIP}. */
  chip: string;
  /** A money cell's figure — see {@link LIST_ROW_VALUE_TEXT}. */
  valueText: string;
  /**
   * The tile the glyph sits in, or `null` for a bare glyph — see
   * {@link ListRowGlyph}, which is what every row should render rather than
   * applying this itself.
   */
  iconTile: string | null;
  /** A detail block's left margin, so it starts under the title. */
  titleOffset: string;
  /** The same distance as padding, for a row nested under another. */
  nestedIndent: string;
  /** The rule between adjacent rows, as a `divide-*` utility on their parent. */
  divider: string;
  /** The fill behind the list's section-header strip. */
  headerFill: string;
}

const RHYTHMS: Record<ListRowRhythmName, ListRowRhythm> = {
  compact: {
    paddingY: LIST_ROW_PADDING_Y,
    paddingYWithDetail: LIST_ROW_PADDING_WITH_DETAIL,
    titleText: LIST_ROW_TITLE_TEXT,
    titleWeight: 'font-medium',
    supportingText: LIST_ROW_SUPPORTING_TEXT,
    metaText: LIST_ROW_META_TEXT,
    icon: LIST_ROW_ICON,
    control: LIST_ROW_CONTROL,
    chip: LIST_ROW_CHIP,
    valueText: LIST_ROW_VALUE_TEXT,
    iconTile: null,
    titleOffset: LIST_ROW_TITLE_OFFSET,
    nestedIndent: LIST_ROW_NESTED_INDENT,
    divider: LIST_ROW_DIVIDER,
    headerFill: LIST_HEADER_FILL
  },
  roomy: {
    paddingY: LIST_ROW_PADDING_Y_ROOMY,
    paddingYWithDetail: LIST_ROW_PADDING_WITH_DETAIL_ROOMY,
    titleText: LIST_ROW_TITLE_TEXT_ANCHORED,
    titleWeight: 'font-semibold',
    supportingText: LIST_ROW_SUPPORTING_TEXT_ROOMY,
    metaText: LIST_ROW_META_TEXT,
    icon: LIST_ROW_ICON_ROOMY,
    control: LIST_ROW_CONTROL_ROOMY,
    chip: LIST_ROW_CHIP_ROOMY,
    valueText: LIST_ROW_VALUE_TEXT_ROOMY,
    iconTile: LIST_ROW_ICON_TILE_ROOMY,
    titleOffset: LIST_ROW_TITLE_OFFSET_ROOMY,
    nestedIndent: LIST_ROW_NESTED_INDENT_ROOMY,
    divider: LIST_ROW_DIVIDER_STRONG,
    headerFill: LIST_HEADER_FILL_STRONG
  }
};

const ListRowRhythmContext = createContext<ListRowRhythmName>('compact');

/**
 * Render the lists beneath this at a named rhythm (VALE-1494).
 *
 * A context rather than a prop because the rows it reaches sit three and four
 * levels below the surface that decides — `BalanceSheet` →
 * `BalanceSheetSection` → `InventoryItemRow`, `ContactList` → `ContactRow` —
 * and threading one boolean through every layer in between would put the
 * decision in four more files that have no stake in it.
 */
export function ListRowRhythmProvider({
  rhythm,
  children
}: {
  rhythm: ListRowRhythmName;
  children: ReactNode;
}) {
  return <ListRowRhythmContext value={rhythm}>{children}</ListRowRhythmContext>;
}

/**
 * The rhythm the enclosing surface asked for, or the app-wide default.
 *
 * Every hand-rolled row binds to this rather than restating `py-2` /
 * `text-sm font-medium` / `h-4 w-4`, which is what keeps every rhythm in one
 * place.
 *
 * **It takes no arguments, and that is the point** (VALE-1534). It used to
 * accept an `own` partial so a row could state the padding its own surface
 * already rendered, because VALE-1494 needed to change the AI review without
 * moving three other tabs as a side effect. The cost was that the shared
 * default reached only the rows that had not opted out of it — so `compact`
 * was the app-wide answer in name while four surfaces quietly rendered
 * something else, which is the exact drift this file exists to prevent.
 *
 * VALE-1534 is the density decision those overrides were deferring. With it
 * made, an override is no longer a deferral, it is a second answer: a surface
 * that opts out cannot be moved by the knob at all, and a knob that moves some
 * of the app is worse than none. So there is one answer per rhythm, and a row
 * that genuinely needs to differ changes the rhythm rather than escaping it.
 */
export function useListRowRhythm(): ListRowRhythm {
  return RHYTHMS[use(ListRowRhythmContext)];
}

/**
 * **A surface's own answer for its section-header strips, overriding the one
 * its row rhythm carries** (VALE-1756).
 *
 * The fill arrived as part of the rhythm, which bundled two decisions that are
 * not the same decision. A rhythm is about **row height** — how much air a row
 * gets, whether its glyph sits in a tile — and a surface picks one from how
 * many rows it expects and how closely each is read. The strip's fill is about
 * **telling one list from the next**, which is a property of the page the lists
 * are stacked on.
 *
 * On the AI-extract review those two answers genuinely disagree. Its documents
 * and entitlements lists take the `compact` rhythm deliberately (VALE-1584: a
 * bulk extract puts thirty file rows above the values the reviewer is here to
 * check), while its contacts, inventory and class-proposal lists take `roomy`.
 * They all stack in one column, so they were reading with two different greys
 * above them — and the review is exactly the case
 * {@link LIST_HEADER_FILL_STRONG} was written for: "a review where the heading
 * is the only thing telling four stacked lists apart".
 *
 * Declared once per surface and read by every strip under it, for the reason
 * `ListRowMarkPlacementProvider` exists: the same `ListSection` renders on the
 * Documents tab and on the review and has to differ, so the answer arrives by
 * context rather than from a prop each call site remembers to pass. A surface
 * that says nothing keeps its rhythm's own fill, so nothing outside the review
 * moves.
 */
const ListHeaderFillContext = createContext<string | null>(null);

export function ListHeaderFillProvider({
  fill,
  children
}: {
  fill: string;
  children: ReactNode;
}) {
  return <ListHeaderFillContext value={fill}>{children}</ListHeaderFillContext>;
}

/** The surface's declared strip fill, or the one this list's rhythm carries. */
export function useListHeaderFill(): string {
  const declared = use(ListHeaderFillContext);
  /* Both read unconditionally — `??` on the right-hand call would make the
     rhythm a conditional hook. */
  const { headerFill } = useListRowRhythm();
  return declared ?? headerFill;
}

/**
 * A row's leading glyph, in whatever housing the surface's rhythm asks for
 * (VALE-1494).
 *
 * **Every hand-rolled row renders its glyph through this**, rather than each
 * deciding whether to draw a tile. Consistency across the four glyph kinds is
 * the whole point of the tile, and four call sites each applying it is four
 * chances for one to be missed or to drift a radius.
 *
 * With no tile in the rhythm it renders the glyph bare, so the Contacts,
 * Documents and Entitlements surfaces are exactly what they were.
 */
export function ListRowGlyph({
  children,
  className
}: {
  children: ReactNode;
  className?: string;
}) {
  const { iconTile } = useListRowRhythm();
  if (!iconTile) return <>{children}</>;
  return <span className={cn(iconTile, className)}>{children}</span>;
}

interface ListRowProps {
  /** Leading glyph/icon/avatar. */
  leading?: ReactNode;
  /** Row title — truncates on overflow. */
  title: ReactNode;
  /** Supplementary content beside the title (status pills, chips). */
  inline?: ReactNode;
  /** Muted trailing text (e.g. a duration/date), after `inline`. */
  meta?: ReactNode;
  /**
   * Right-aligned state token (e.g. a section status pill), rendered immediately
   * left of `actions` — so a single contextual pill reads against the row's CTA
   * instead of trailing the name (LF-6095). Unlike `actions` this is deliberately
   * NOT click-isolated: a status pill is a label, not a control, so clicking it
   * opens the row like the rest of the row does.
   */
  status?: ReactNode;
  /** Right-aligned actions (CTA, avatar, overflow menu) — clicks here don't open the row. */
  actions?: ReactNode;
  /** Opens the row (e.g. navigate to detail). */
  onClick: () => void;
  /** Accessible name for the row's `role="button"`. */
  'aria-label': string;
  className?: string;
}

export function ListRow({
  leading,
  title,
  inline,
  meta,
  status,
  actions,
  onClick,
  'aria-label': ariaLabel,
  className
}: ListRowProps) {
  const rhythm = useListRowRhythm();
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={cn(
        'group hover:bg-surface-subtle flex cursor-pointer items-center gap-2 pr-0 pl-1.5 text-left transition-colors',
        rhythm.paddingY,
        className
      )}
    >
      <div className="flex min-w-0 flex-1 items-center gap-2.5">
        {leading}
        <span
          className={cn('text-foreground min-w-0 truncate', rhythm.titleText)}
        >
          {title}
        </span>
        {inline}
        {!!meta && (
          <span
            className={cn('text-muted-foreground shrink-0', rhythm.metaText)}
          >
            {meta}
          </span>
        )}
      </div>

      {!!status && <div className="flex shrink-0 items-center">{status}</div>}

      {!!actions && (
        <div
          className="flex shrink-0 items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {actions}
        </div>
      )}
    </div>
  );
}
